interface WeatherIconProps {
  icon: string;
  size?: number;
  className?: string;
}

export function WeatherIcon({ icon, size = 48, className }: WeatherIconProps) {
  const reducedMotion =
    typeof window !== "undefined"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : true;

  const animClass = reducedMotion ? "" : " animated";

  switch (icon) {
    case "clear-day":
      return (
        <span className={className} style={{ display: "inline-flex" }}>
          <svg
            width={size}
            height={size}
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <style>{`
              @keyframes spin-slow { to { transform: rotate(360deg); } }
              .sun-group${animClass} { animation: spin-slow 20s linear infinite; transform-origin: 24px 24px; }
              @media (prefers-reduced-motion: reduce) { .sun-group { animation: none !important; } }
            `}</style>
            <g className={`sun-group${animClass}`}>
              <circle cx="24" cy="24" r="9" fill="#FBBF24" />
              {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
                <line
                  key={deg}
                  x1="24"
                  y1="4"
                  x2="24"
                  y2="10"
                  stroke="#FBBF24"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  transform={`rotate(${deg} 24 24)`}
                />
              ))}
            </g>
          </svg>
        </span>
      );

    case "clear-night":
      return (
        <span className={className} style={{ display: "inline-flex" }}>
          <svg
            width={size}
            height={size}
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M30 8C22 8 16 14 16 22C16 30 22 36 30 36C26 36 20 32 18 26C16 20 18 12 24 10C22 9 21 8 20 8C14 8 9 13 9 20C9 28 16 35 24 35C32 35 38 29 38 21C38 14 35 9 30 8Z"
              fill="#CBD5E1"
            />
          </svg>
        </span>
      );

    case "partly-cloudy-day":
      return (
        <span className={className} style={{ display: "inline-flex" }}>
          <svg
            width={size}
            height={size}
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            {/* Small sun behind cloud */}
            <circle cx="16" cy="17" r="7" fill="#FBBF24" />
            {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
              <line
                key={deg}
                x1="16"
                y1="7"
                x2="16"
                y2="10"
                stroke="#FBBF24"
                strokeWidth="2"
                strokeLinecap="round"
                transform={`rotate(${deg} 16 17)`}
              />
            ))}
            {/* Cloud */}
            <path
              d="M14 38C9.6 38 6 34.4 6 30C6 25.9 9.1 22.5 13.1 22.1C13.9 19.2 16.5 17 19.5 17C21.2 17 22.8 17.6 24 18.7C25 16 27.5 14 30.5 14C34.6 14 38 17.4 38 21.5C38 21.7 38 21.9 38 22.1C40.2 22.9 42 25 42 27.5C42 30.5 39.5 33 36.5 33H14Z"
              fill="white"
              stroke="#E2E8F0"
              strokeWidth="1"
            />
          </svg>
        </span>
      );

    case "cloudy":
      return (
        <span className={className} style={{ display: "inline-flex" }}>
          <svg
            width={size}
            height={size}
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M12 38C7.6 38 4 34.4 4 30C4 25.9 7.1 22.5 11.1 22.1C11.9 19.2 14.5 17 17.5 17C19.2 17 20.8 17.6 22 18.7C23 16 25.5 14 28.5 14C32.6 14 36 17.4 36 21.5V22C38.2 22.7 40 24.7 40 27.2C40 30.2 37.5 32.6 34.5 32.6H12V38Z"
              fill="#94A3B8"
            />
            <path
              d="M12 36C7.6 36 4 32.4 4 28C4 23.9 7.1 20.5 11.1 20.1C11.9 17.2 14.5 15 17.5 15C19.2 15 20.8 15.6 22 16.7C23 14 25.5 12 28.5 12C32.6 12 36 15.4 36 19.5V20C38.2 20.7 40 22.7 40 25.2C40 28.2 37.5 30.6 34.5 30.6H12V36Z"
              fill="#CBD5E1"
            />
          </svg>
        </span>
      );

    case "fog":
      return (
        <span className={className} style={{ display: "inline-flex" }}>
          <svg
            width={size}
            height={size}
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <line x1="8" y1="18" x2="40" y2="18" stroke="#94A3B8" strokeWidth="3" strokeLinecap="round" />
            <line x1="6" y1="26" x2="42" y2="26" stroke="#CBD5E1" strokeWidth="3" strokeLinecap="round" />
            <line x1="10" y1="34" x2="38" y2="34" stroke="#94A3B8" strokeWidth="3" strokeLinecap="round" />
          </svg>
        </span>
      );

    case "drizzle":
      return (
        <span className={className} style={{ display: "inline-flex" }}>
          <svg
            width={size}
            height={size}
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <style>{`
              @keyframes drip { 0%{transform:translateY(0);opacity:1} 80%{opacity:1} 100%{transform:translateY(6px);opacity:0} }
              .drop0${animClass}{animation:drip 1.2s ease-in 0s infinite}
              .drop1${animClass}{animation:drip 1.2s ease-in 0.3s infinite}
              .drop2${animClass}{animation:drip 1.2s ease-in 0.6s infinite}
              @media (prefers-reduced-motion: reduce) { .drop0,.drop1,.drop2 { animation: none !important; } }
            `}</style>
            {/* Cloud */}
            <path d="M10 26C6.7 26 4 23.3 4 20C4 16.9 6.3 14.3 9.3 14C10 11.7 12.1 10 14.5 10C15.8 10 17 10.4 17.9 11.2C18.7 9.3 20.6 8 22.8 8C26 8 28.5 10.5 28.5 13.7V14C30.2 14.5 31.5 16.1 31.5 18C31.5 20.5 29.4 22.5 26.9 22.5H10V26Z" fill="#CBD5E1" />
            <path d="M10 24C6.7 24 4 21.3 4 18C4 14.9 6.3 12.3 9.3 12C10 9.7 12.1 8 14.5 8C15.8 8 17 8.4 17.9 9.2C18.7 7.3 20.6 6 22.8 6C26 6 28.5 8.5 28.5 11.7V12C30.2 12.5 31.5 14.1 31.5 16C31.5 18.5 29.4 20.5 26.9 20.5H10V24Z" fill="#E2E8F0" />
            {/* Drops */}
            <line x1="12" y1="29" x2="10" y2="34" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round" className={`drop0${animClass}`} />
            <line x1="19" y1="29" x2="17" y2="34" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round" className={`drop1${animClass}`} />
            <line x1="26" y1="29" x2="24" y2="34" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round" className={`drop2${animClass}`} />
          </svg>
        </span>
      );

    case "rain":
      return (
        <span className={className} style={{ display: "inline-flex" }}>
          <svg
            width={size}
            height={size}
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <style>{`
              @keyframes drip-rain { 0%{transform:translateY(0);opacity:1} 80%{opacity:1} 100%{transform:translateY(8px);opacity:0} }
              .rdrop0${animClass}{animation:drip-rain 1s ease-in 0s infinite}
              .rdrop1${animClass}{animation:drip-rain 1s ease-in 0.2s infinite}
              .rdrop2${animClass}{animation:drip-rain 1s ease-in 0.4s infinite}
              @media (prefers-reduced-motion: reduce) { .rdrop0,.rdrop1,.rdrop2 { animation: none !important; } }
            `}</style>
            <path d="M10 28C6.7 28 4 25.3 4 22C4 18.9 6.3 16.3 9.3 16C10 13.7 12.1 12 14.5 12C15.8 12 17 12.4 17.9 13.2C18.7 11.3 20.6 10 22.8 10C26 10 28.5 12.5 28.5 15.7V16C30.2 16.5 31.5 18.1 31.5 20C31.5 22.5 29.4 24.5 26.9 24.5H10V28Z" fill="#CBD5E1" />
            <path d="M10 26C6.7 26 4 23.3 4 20C4 16.9 6.3 14.3 9.3 14C10 11.7 12.1 10 14.5 10C15.8 10 17 10.4 17.9 11.2C18.7 9.3 20.6 8 22.8 8C26 8 28.5 10.5 28.5 13.7V14C30.2 14.5 31.5 16.1 31.5 18C31.5 20.5 29.4 22.5 26.9 22.5H10V26Z" fill="#E2E8F0" />
            <line x1="10" y1="31" x2="8" y2="37" stroke="#3B82F6" strokeWidth="2.5" strokeLinecap="round" className={`rdrop0${animClass}`} />
            <line x1="18" y1="31" x2="16" y2="37" stroke="#3B82F6" strokeWidth="2.5" strokeLinecap="round" className={`rdrop1${animClass}`} />
            <line x1="26" y1="31" x2="24" y2="37" stroke="#3B82F6" strokeWidth="2.5" strokeLinecap="round" className={`rdrop2${animClass}`} />
          </svg>
        </span>
      );

    case "heavy-rain":
      return (
        <span className={className} style={{ display: "inline-flex" }}>
          <svg
            width={size}
            height={size}
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <style>{`
              @keyframes drip-heavy { 0%{transform:translateY(0);opacity:1} 80%{opacity:0.8} 100%{transform:translateY(10px);opacity:0} }
              .hdrop0${animClass}{animation:drip-heavy 0.8s ease-in 0s infinite}
              .hdrop1${animClass}{animation:drip-heavy 0.8s ease-in 0.15s infinite}
              .hdrop2${animClass}{animation:drip-heavy 0.8s ease-in 0.3s infinite}
              .hdrop3${animClass}{animation:drip-heavy 0.8s ease-in 0.45s infinite}
              .hdrop4${animClass}{animation:drip-heavy 0.8s ease-in 0.6s infinite}
              @media (prefers-reduced-motion: reduce) { .hdrop0,.hdrop1,.hdrop2,.hdrop3,.hdrop4 { animation: none !important; } }
            `}</style>
            <path d="M8 26C4.7 26 2 23.3 2 20C2 16.9 4.3 14.3 7.3 14C8 11.7 10.1 10 12.5 10C13.8 10 15 10.4 15.9 11.2C16.7 9.3 18.6 8 20.8 8C24 8 26.5 10.5 26.5 13.7V14C28.2 14.5 29.5 16.1 29.5 18C29.5 20.5 27.4 22.5 24.9 22.5H8V26Z" fill="#94A3B8" />
            <path d="M8 24C4.7 24 2 21.3 2 18C2 14.9 4.3 12.3 7.3 12C8 9.7 10.1 8 12.5 8C13.8 8 15 8.4 15.9 9.2C16.7 7.3 18.6 6 20.8 6C24 6 26.5 8.5 26.5 11.7V12C28.2 12.5 29.5 14.1 29.5 16C29.5 18.5 27.4 20.5 24.9 20.5H8V24Z" fill="#CBD5E1" />
            <line x1="6" y1="29" x2="4" y2="36" stroke="#2563EB" strokeWidth="2.5" strokeLinecap="round" className={`hdrop0${animClass}`} />
            <line x1="12" y1="29" x2="10" y2="36" stroke="#2563EB" strokeWidth="2.5" strokeLinecap="round" className={`hdrop1${animClass}`} />
            <line x1="18" y1="29" x2="16" y2="36" stroke="#2563EB" strokeWidth="2.5" strokeLinecap="round" className={`hdrop2${animClass}`} />
            <line x1="24" y1="29" x2="22" y2="36" stroke="#2563EB" strokeWidth="2.5" strokeLinecap="round" className={`hdrop3${animClass}`} />
            <line x1="30" y1="29" x2="28" y2="36" stroke="#2563EB" strokeWidth="2.5" strokeLinecap="round" className={`hdrop4${animClass}`} />
          </svg>
        </span>
      );

    case "thunderstorm":
      return (
        <span className={className} style={{ display: "inline-flex" }}>
          <svg
            width={size}
            height={size}
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <style>{`
              @keyframes flash { 0%,100%{opacity:1} 50%{opacity:0.1} }
              .bolt${animClass}{animation:flash 3s steps(1) infinite}
              @media (prefers-reduced-motion: reduce) { .bolt { animation: none !important; } }
            `}</style>
            <path d="M10 28C6.7 28 4 25.3 4 22C4 18.9 6.3 16.3 9.3 16C10 13.7 12.1 12 14.5 12C15.8 12 17 12.4 17.9 13.2C18.7 11.3 20.6 10 22.8 10C26 10 28.5 12.5 28.5 15.7V16C30.2 16.5 31.5 18.1 31.5 20C31.5 22.5 29.4 24.5 26.9 24.5H10V28Z" fill="#64748B" />
            <path d="M10 26C6.7 26 4 23.3 4 20C4 16.9 6.3 14.3 9.3 14C10 11.7 12.1 10 14.5 10C15.8 10 17 10.4 17.9 11.2C18.7 9.3 20.6 8 22.8 8C26 8 28.5 10.5 28.5 13.7V14C30.2 14.5 31.5 16.1 31.5 18C31.5 20.5 29.4 22.5 26.9 22.5H10V26Z" fill="#94A3B8" />
            {/* Lightning bolt */}
            <path d="M19 27L14 35H18L15 43L24 32H20L23 27Z" fill="#FBBF24" className={`bolt${animClass}`} />
            {/* Rain drops */}
            <line x1="9" y1="29" x2="7" y2="34" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round" />
            <line x1="27" y1="29" x2="25" y2="34" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </span>
      );

    case "snow":
      return (
        <span className={className} style={{ display: "inline-flex" }}>
          <svg
            width={size}
            height={size}
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path d="M10 26C6.7 26 4 23.3 4 20C4 16.9 6.3 14.3 9.3 14C10 11.7 12.1 10 14.5 10C15.8 10 17 10.4 17.9 11.2C18.7 9.3 20.6 8 22.8 8C26 8 28.5 10.5 28.5 13.7V14C30.2 14.5 31.5 16.1 31.5 18C31.5 20.5 29.4 22.5 26.9 22.5H10V26Z" fill="#E2E8F0" />
            {/* Snowflake */}
            <line x1="18" y1="30" x2="18" y2="44" stroke="#BAE6FD" strokeWidth="2" strokeLinecap="round" />
            <line x1="11" y1="33.5" x2="25" y2="40.5" stroke="#BAE6FD" strokeWidth="2" strokeLinecap="round" />
            <line x1="11" y1="40.5" x2="25" y2="33.5" stroke="#BAE6FD" strokeWidth="2" strokeLinecap="round" />
            <circle cx="18" cy="30" r="1.5" fill="#93C5FD" />
            <circle cx="18" cy="44" r="1.5" fill="#93C5FD" />
            <circle cx="11" cy="33.5" r="1.5" fill="#93C5FD" />
            <circle cx="25" cy="40.5" r="1.5" fill="#93C5FD" />
            <circle cx="11" cy="40.5" r="1.5" fill="#93C5FD" />
            <circle cx="25" cy="33.5" r="1.5" fill="#93C5FD" />
          </svg>
        </span>
      );

    case "windy":
      return (
        <span className={className} style={{ display: "inline-flex" }}>
          <svg
            width={size}
            height={size}
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path d="M4 17C4 17 8 12 14 14C20 16 18 22 24 22C30 22 30 17 36 17" stroke="#94A3B8" strokeWidth="2.5" strokeLinecap="round" fill="none" />
            <path d="M4 24C4 24 10 20 16 22C22 24 20 30 26 30C32 30 32 24 40 24" stroke="#CBD5E1" strokeWidth="2.5" strokeLinecap="round" fill="none" />
            <path d="M4 31C4 31 7 27 12 29C17 31 16 36 22 36C26 36 28 33 32 33" stroke="#94A3B8" strokeWidth="2.5" strokeLinecap="round" fill="none" />
          </svg>
        </span>
      );

    default:
      // Fallback: circle with question mark
      return (
        <span className={className} style={{ display: "inline-flex" }}>
          <svg
            width={size}
            height={size}
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <circle cx="24" cy="24" r="20" stroke="#94A3B8" strokeWidth="2" fill="none" />
            <text x="24" y="31" textAnchor="middle" fontSize="20" fill="#94A3B8" fontFamily="sans-serif" fontWeight="bold">?</text>
          </svg>
        </span>
      );
  }
}
